/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import { AnyAction } from 'redux';

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';

import { CHANNEL_NUMBER_MAX_LENGTH, DEFAULT_RETRY_OPTIONS } from '../../../config';
import { ServicesClientImpl } from '../../../core/interactors/ServicesClientImpl';
import { Log } from '../../../core/logging/Log';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import { RetryMechanism } from '../../../core/retrymechanism/RetryMechanism';
import { RetryOptions } from '../../../core/retrymechanism/RetryOptions';
import { TChannels } from '../../../core/types/TChannels';
import { showTemporaryGuideIssueOverlay } from '../../overlay/temporaryguideissueoverlay/temporaryguideissueoverlay.reducers';
import { setScreenTaskController } from '../../screens/screens.reducers';
import { setGridGuideOffsetAction, setIsChannelsServiceFailAction } from '../guide.actions';
import { loadGridGuideChannels } from '../guide.reducers';
import { setVerticalPosition } from '../guidehighlight/guidehighlight.reducers';
import {
    setEnteredChannelNumberAction,
    startEnterChannelNumberAction,
    stopEnterChannelNumberAction,
} from './enterchannelnumber.actions';
import {
    GUIDE_SET_ENTERED_CHANNEL_NUMBER,
    START_ENTER_CHANNEL_NUMBER,
    STOP_ENTER_CHANNEL_NUMBER,
} from './enterchannelnumber.types';

const TAG = 'enterChannelNumber';
const initialState: {
    isEnterChannelNumberActive: boolean;
    enteredChannelNumber: string;
} = { isEnterChannelNumberActive: false, enteredChannelNumber: '' };

export const enterChannelNumberReducer = (
    state = initialState,
    { type, payload } = <AnyAction>{},
) => {
    switch (type) {
        case START_ENTER_CHANNEL_NUMBER:
        case STOP_ENTER_CHANNEL_NUMBER:
            return {
                ...state,
                isEnterChannelNumberActive: payload.isEnterChannelNumberActive,
                enteredChannelNumber: payload.enteredChannelNumber,
            };
        case GUIDE_SET_ENTERED_CHANNEL_NUMBER:
            return {
                ...state,
                enteredChannelNumber: payload.enteredChannelNumber,
            };
        default:
            return state;
    }
};

export const startEnterChannelNumber = () => (dispatch: Function) => {
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.ENTER_CHANNEL_NUMBER));
    dispatch(startEnterChannelNumberAction());
};

export const stopEnterChannelNumber = () => (dispatch: Function) => {
    dispatch(stopEnterChannelNumberAction());
    dispatch(setVerticalPosition(0));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS));
};

export const setEnteredChannelNumber = () => (dispatch: Function, getState: Function) => {
    const { key } = getState().inputFactory;
    const { enteredChannelNumber } = getState().enterChannelNumber;

    if (enteredChannelNumber.length < CHANNEL_NUMBER_MAX_LENGTH) {
        dispatch(setEnteredChannelNumberAction(`${enteredChannelNumber}${key}`));
    }
};

export const fetchAndUpdateChannelsFromChannelNumber =
    (channelNumber: string) => (dispatch: Function, getState: Function) => {
        if (channelNumber.length === 0) return;

        const { LIMIT } = getState().gridGuide;
        const { channelsType } = getState().channelOptionsOverlay;

        const services: ServicesClientImpl = new ServicesClientImpl();
        const fetchNextChannelsFromChannelNumber =
            services.fetchNextChannelsFromChannelNumber.bind(services);
        RetryMechanism.callServiceWithRetry<TChannels>(
            fetchNextChannelsFromChannelNumber,
            [channelNumber, channelsType, LIMIT],
            <RetryOptions>DEFAULT_RETRY_OPTIONS,
        )
            .then((response: TChannels) => {
                dispatch(setGridGuideOffsetAction(response.currentOffset));
                dispatch(loadGridGuideChannels());
            })
            .catch((error) => {
                dispatch(setIsChannelsServiceFailAction(true));
                dispatch(showTemporaryGuideIssueOverlay());
                Diagnostics.error(
                    TAG,
                    'Failed to fetch next channels from channel number.fetchAndUpdateChannelsFromChannelNumber() -> services.fetchNextChannelsFromChannelNumber',
                    error,
                );
                Log.e(TAG, error);
            });
    };

export const onKeyOkEnterChannelNumber = () => (dispatch: Function, getState: Function) => {
    const { enteredChannelNumber } = getState().enterChannelNumber;
    dispatch(fetchAndUpdateChannelsFromChannelNumber(enteredChannelNumber));
    dispatch(stopEnterChannelNumber());
};

export default enterChannelNumberReducer;
